import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Doc, Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { cn } from '@rekrytering/ui/lib/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type CandidateStatus = Doc<'candidates'>['status']

const STATUS_STYLES: Record<CandidateStatus, string> = {
  invited: 'bg-blue-100 text-blue-700',
  started: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  expired: 'bg-gray-100 text-gray-600',
  disqualified: 'bg-orange-100 text-orange-700',
  rejected: 'bg-red-100 text-red-700',
  advanced: 'bg-purple-100 text-purple-700',
}

const STATUS_I18N_KEYS = {
  invited: 'interviews.candidateActions.statusInvited',
  started: 'interviews.candidateActions.statusStarted',
  completed: 'interviews.candidateActions.statusCompleted',
  expired: 'interviews.candidateActions.statusExpired',
  disqualified: 'interviews.candidateActions.statusDisqualified',
  rejected: 'interviews.candidateActions.statusRejected',
  advanced: 'interviews.candidateActions.statusAdvanced',
} as const satisfies Record<CandidateStatus, string>

type RejectDialogState =
  | { readonly open: false }
  | { readonly open: true; readonly candidateId: Id<'candidates'> }

type BulkRejectDialogState =
  | { readonly open: false }
  | { readonly open: true; readonly candidateIds: readonly Id<'candidates'>[] }

type ConfirmDialogState =
  | { readonly open: false }
  | {
      readonly open: true
      readonly candidateId: Id<'candidates'>
      readonly action: 'advance' | 'reTake'
    }

type Props = {
  readonly jobId: Id<'jobs'>
  readonly hasCalendlyUrl: boolean
  readonly calendlyUrl: string | undefined
}

export const CandidateActionsTab = ({
  jobId,
  hasCalendlyUrl,
  calendlyUrl,
}: Props) => {
  const { t } = useTranslation()
  const [calendlyInput, setCalendlyInput] = useState(calendlyUrl ?? '')
  const [rejectDialog, setRejectDialog] = useState<RejectDialogState>({
    open: false,
  })
  const [bulkRejectDialog, setBulkRejectDialog] =
    useState<BulkRejectDialogState>({ open: false })
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
  })
  const [customMessage, setCustomMessage] = useState('')
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<Id<'candidates'>>>(
    new Set(),
  )

  const { data: candidates = [] } = useQuery(
    convexQuery(api.features.candidates.candidates.listMyCandidatesForJob, {
      jobId,
    }),
  )

  const { mutateAsync: rejectCandidate } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidates.rejectMyCandidate,
    ),
  })

  const { mutateAsync: advanceCandidate } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidates.advanceMyCandidateToHumanInterview,
    ),
  })

  const { mutateAsync: requestReTake } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidates.requestMyInterviewReTake,
    ),
  })

  const { mutateAsync: bulkReject } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidates.bulkRejectMyCandidates,
    ),
  })

  const { mutateAsync: bulkAdvance } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidates.bulkAdvanceMyCandidates,
    ),
  })

  const { mutateAsync: bulkSendReminder } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidates.bulkSendMyReminders,
    ),
  })

  const { mutateAsync: updateJob } = useMutation({
    mutationFn: useConvexMutation(api.features.jobs.jobs.updateMyJob),
  })

  const handleSaveCalendlyUrl = async (): Promise<void> => {
    await updateJob({
      jobId,
      calendlyUrl: calendlyInput.trim() || undefined,
    })
  }

  const handleRejectConfirm = async (): Promise<void> => {
    if (!rejectDialog.open) return
    await rejectCandidate({
      candidateId: rejectDialog.candidateId,
      customMessage: customMessage.trim() || undefined,
    })
    setRejectDialog({ open: false })
    setCustomMessage('')
  }

  const handleConfirmAction = async (): Promise<void> => {
    if (!confirmDialog.open) return
    if (confirmDialog.action === 'advance') {
      await advanceCandidate({ candidateId: confirmDialog.candidateId })
    } else {
      await requestReTake({ candidateId: confirmDialog.candidateId })
    }
    setConfirmDialog({ open: false })
  }

  const handleBulkRejectConfirm = async (): Promise<void> => {
    if (!bulkRejectDialog.open) return
    await bulkReject({
      candidateIds: [...bulkRejectDialog.candidateIds],
      customMessage: customMessage.trim() || undefined,
    })
    setBulkRejectDialog({ open: false })
    setCustomMessage('')
    setSelectedIds(new Set())
  }

  const handleBulkAdvance = async (): Promise<void> => {
    await bulkAdvance({ candidateIds: [...selectedIds] })
    setSelectedIds(new Set())
  }

  const handleBulkSendReminder = async (): Promise<void> => {
    await bulkSendReminder({ candidateIds: [...selectedIds] })
    setSelectedIds(new Set())
  }

  const handleToggleSelect = (id: Id<'candidates'>): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleToggleSelectAll = (): void => {
    if (selectedIds.size === candidates.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(candidates.map((c) => c._id)))
    }
  }

  const allSelected =
    candidates.length > 0 && selectedIds.size === candidates.length

  return (
    <>
      <div className="mb-4 flex flex-col gap-2 rounded-lg border border-border p-4">
        <label className="text-sm font-medium text-primary">
          {t('interviews.candidateActions.calendlyUrlLabel')}
        </label>
        <p className="text-xs text-primary/60">
          {t('interviews.candidateActions.calendlyUrlDescription')}
        </p>
        <div className="flex gap-2">
          <Input
            value={calendlyInput}
            onChange={(e) => setCalendlyInput(e.target.value)}
            placeholder="https://calendly.com/..."
            className="flex-1"
          />
          <Button size="sm" onClick={handleSaveCalendlyUrl}>
            {t('interviews.candidateActions.saveCalendlyUrl')}
          </Button>
        </div>
      </div>

      {candidates.length === 0 ? (
        <div className="flex items-center justify-center py-24 text-primary/40">
          <p className="text-sm">
            {t('interviews.candidateActions.noCandidates')}
          </p>
        </div>
      ) : (
        <>
          {selectedIds.size > 0 && (
            <div className="mb-3 flex items-center gap-3 rounded-lg border border-border bg-secondary/40 px-4 py-2">
              <span className="text-xs font-medium text-primary/60">
                {t('interviews.candidateActions.selected', {
                  count: selectedIds.size,
                })}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setBulkRejectDialog({
                      open: true,
                      candidateIds: [...selectedIds],
                    })
                  }
                >
                  {t('interviews.candidateActions.bulkReject')}
                </Button>
                {hasCalendlyUrl && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkAdvance}
                  >
                    {t('interviews.candidateActions.bulkAdvance')}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBulkSendReminder}
                >
                  {t('interviews.candidateActions.bulkSendReminder')}
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3 px-4 pb-1">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleToggleSelectAll}
                className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
              />
              <span className="text-xs text-primary/40">
                {allSelected
                  ? t('interviews.candidateActions.selected', {
                      count: candidates.length,
                    })
                  : ''}
              </span>
            </div>
            {candidates.map((candidate) => (
              <div
                key={candidate._id}
                className={cn(
                  'flex items-center justify-between gap-4 rounded-lg border border-border px-4 py-3',
                  selectedIds.has(candidate._id) && 'bg-secondary/40',
                )}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(candidate._id)}
                    onChange={() => handleToggleSelect(candidate._id)}
                    className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
                  />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-primary">
                      {candidate.name}
                    </span>
                    <span className="text-xs text-primary/50">
                      {candidate.email}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-0.5 text-xs font-medium',
                      STATUS_STYLES[candidate.status],
                    )}
                  >
                    {t(STATUS_I18N_KEYS[candidate.status])}
                  </span>

                  <div className="flex gap-2">
                    {(candidate.status === 'completed' ||
                      candidate.status === 'expired') && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setConfirmDialog({
                            open: true,
                            candidateId: candidate._id,
                            action: 'reTake',
                          })
                        }
                      >
                        {t('interviews.candidateActions.reTake')}
                      </Button>
                    )}

                    {candidate.status === 'completed' && hasCalendlyUrl && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setConfirmDialog({
                            open: true,
                            candidateId: candidate._id,
                            action: 'advance',
                          })
                        }
                      >
                        {t('interviews.candidateActions.advance')}
                      </Button>
                    )}

                    {(candidate.status === 'completed' ||
                      candidate.status === 'started' ||
                      candidate.status === 'expired') && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setCustomMessage('')
                          setRejectDialog({
                            open: true,
                            candidateId: candidate._id,
                          })
                        }}
                      >
                        {t('interviews.candidateActions.reject')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {rejectDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-lg">
            <h3 className="mb-1 text-base font-semibold text-primary">
              {t('interviews.candidateActions.rejectDialogTitle')}
            </h3>
            <p className="mb-4 text-sm text-primary/60">
              {t('interviews.candidateActions.rejectDialogDescription')}
            </p>
            <textarea
              className="mb-4 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-primary placeholder:text-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20"
              rows={4}
              placeholder={t(
                'interviews.candidateActions.rejectCustomMessagePlaceholder',
              )}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRejectDialog({ open: false })}
              >
                {t('interviews.candidateActions.cancel')}
              </Button>
              <Button size="sm" onClick={handleRejectConfirm}>
                {t('interviews.candidateActions.rejectConfirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {bulkRejectDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-lg">
            <h3 className="mb-1 text-base font-semibold text-primary">
              {t('interviews.candidateActions.bulkRejectDialogTitle')}
            </h3>
            <p className="mb-4 text-sm text-primary/60">
              {t('interviews.candidateActions.bulkRejectDialogDescription', {
                count: bulkRejectDialog.candidateIds.length,
              })}
            </p>
            <textarea
              className="mb-4 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-primary placeholder:text-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20"
              rows={4}
              placeholder={t(
                'interviews.candidateActions.rejectCustomMessagePlaceholder',
              )}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBulkRejectDialog({ open: false })}
              >
                {t('interviews.candidateActions.cancel')}
              </Button>
              <Button size="sm" onClick={handleBulkRejectConfirm}>
                {t('interviews.candidateActions.bulkRejectConfirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-lg">
            <h3 className="mb-1 text-base font-semibold text-primary">
              {confirmDialog.action === 'advance'
                ? t('interviews.candidateActions.advanceConfirmTitle')
                : t('interviews.candidateActions.reTakeConfirmTitle')}
            </h3>
            <p className="mb-6 text-sm text-primary/60">
              {confirmDialog.action === 'advance'
                ? t('interviews.candidateActions.advanceConfirmDescription')
                : t('interviews.candidateActions.reTakeConfirmDescription')}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmDialog({ open: false })}
              >
                {t('interviews.candidateActions.cancel')}
              </Button>
              <Button size="sm" onClick={handleConfirmAction}>
                {confirmDialog.action === 'advance'
                  ? t('interviews.candidateActions.advanceConfirm')
                  : t('interviews.candidateActions.reTakeConfirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
